import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout/Layout';
import api from '../../../api/axios';
import { downloadFile, fmtEUR, fmtDateTime } from '../../../utils/download';
import '../../superadmin/Dashboard/SuperAdminDashboard.css';

function monthStr(offset = 0) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function FairUse({ month }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/fair-use/usage', { params: { month } })
      .then(res => { if (active) setUsage(res.data); })
      .catch(() => { if (active) setUsage({ modules: [] }); });
    return () => { active = false; };
  }, [month]);

  const stateLabel = { ok: 'im Rahmen', warning: 'Vorwarnung', paused: 'pausiert' };
  const stateColor = { ok: 'var(--primary)', warning: '#f59e0b', paused: 'var(--error)' };

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>KI-Verbrauch (Fair Use, AGB § 6)</h2>
        {usage?.modules?.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => downloadFile('/fair-use/usage', `KI-Verbrauch-${month}.csv`, { month, format: 'csv' }).catch(() => alert('Export fehlgeschlagen'))}>
            Einzelverbrauch als CSV
          </button>
        )}
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', margin: '0.25rem 0 1rem' }}>
        Die LLM-Kosten werden je Modul monatlich gegen das Fair-Use-Budget gerechnet. Bei Erreichen des Budgets werden nur die
        KI-Funktionen bis zum Monatsende pausiert; Sie werden vorab gewarnt.
      </p>
      {!usage ? <div>Laden...</div> : usage.modules.length === 0 ? (
        <p style={{ color: 'var(--gray-dark)', fontSize: '0.9rem' }}>Kein KI-Modul gebucht, es fallen keine KI-Kosten an.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {usage.modules.map(m => (
            <div key={m.module}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem 1rem', flexWrap: 'wrap', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <strong style={{ whiteSpace: 'nowrap' }}>{m.module}</strong>
                <span>
                  {fmtEUR(m.cost)} {m.budget > 0 ? `von ${fmtEUR(m.budget)} (${Math.round(m.ratio * 100)} %)` : '(kein Budget hinterlegt)'}
                  {' · '}<span style={{ color: stateColor[m.state], fontWeight: 600 }}>{stateLabel[m.state]}</span>
                </span>
              </div>
              <div style={{ background: 'var(--light)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, (m.ratio || 0) * 100)}%`, background: stateColor[m.state], height: '100%' }} />
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-dark)', marginTop: '0.2rem' }}>{m.calls} Aufrufe · {m.tokensIn + m.tokensOut} Tokens</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CustomerBilling() {
  const [month, setMonth] = useState(monthStr(-1));
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/billing/my-statement', { params: { month } })
      .then(res => { if (active) setStatement(res.data); })
      .catch(() => { if (active) setStatement(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month]);

  async function exportStatement(format) {
    setExporting(format);
    try {
      await downloadFile('/billing/my-statement', `Nutzungseinheiten-${month}.${format}`, { month, format });
    } catch {
      alert('Export fehlgeschlagen');
    } finally { setExporting(''); }
  }

  const c = statement?.customer;
  const licenseRows = (statement?.rows || []).filter(r => r.type === 'Zugangslizenz');
  const courseRows = (statement?.rows || []).filter(r => r.type === 'Kurslizenz');

  return (
    <Layout title="Abrechnung">
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <label>
          Monat:&nbsp;
          <input type="month" value={month} max={monthStr(0)} onChange={e => e.target.value && setMonth(e.target.value)} />
        </label>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" disabled={!!exporting || !c} onClick={() => exportStatement('pdf')}>
          {exporting === 'pdf' ? 'Wird erstellt…' : 'Aufstellung als PDF'}
        </button>
        <button className="btn btn-ghost btn-sm" disabled={!!exporting || !c} onClick={() => exportStatement('csv')}>
          {exporting === 'csv' ? 'Wird erstellt…' : 'Aufstellung als CSV'}
        </button>
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
        Aufstellung der abgerechneten Nutzungseinheiten gemäß AGB § 5 (6) h. Zugangslizenzen je eingerichtetem Nutzer
        (Beendigung wirkt zum Monatsende), Kurslizenzen je Zuweisung mit monatlicher Rate bis zum Laufzeitende, im
        Zuweisungsmonat anteilig. Rücknahmen innerhalb von 14 Tagen werden gutgeschrieben.
      </p>

      {loading ? <div>Laden...</div> : !c ? (
        <div className="alert alert-error">Für diesen Monat liegen keine Abrechnungsdaten vor.</div>
      ) : (
        <>
          {statement.frozen ? (
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              🔒 Abgerechneter Monat (Report eingefroren am {fmtDateTime(statement.frozenAt)})
            </div>
          ) : (
            <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
              Vorläufige Berechnung, der Monat ist noch nicht abgerechnet.
            </div>
          )}

          <div className="sad-stats" style={{ marginBottom: '1.5rem' }}>
            <div className="stat-card"><span className="stat-value">{fmtEUR(c.licenseTotal)}</span><span className="stat-label">Zugangslizenzen ({c.licenseActive})</span></div>
            <div className="stat-card"><span className="stat-value">{fmtEUR(c.trainingsTotal)}</span><span className="stat-label">Kurslizenzen{c.trainingsCredit ? ` (inkl. Gutschrift −${fmtEUR(c.trainingsCredit)})` : ''}</span></div>
            <div className="stat-card"><span className="stat-value">{fmtEUR(c.total)}</span><span className="stat-label">Gesamt {statement.label}</span></div>
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Zugangslizenzen · {fmtEUR(c.licenseCostPerUser)} je Nutzer und Monat</h2>
            <div className="table-container">
              <table>
                <thead><tr><th>Nutzer</th><th>E-Mail</th><th>Von</th><th>Bis</th><th>Tage</th><th>Status</th><th style={{ textAlign: 'right' }}>Betrag</th></tr></thead>
                <tbody>
                  {licenseRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.userName}</td><td>{r.userEmail}</td><td>{r.from}</td><td>{r.to}</td><td>{r.days}</td><td>{r.status}</td>
                      <td style={{ textAlign: 'right' }}>{fmtEUR(r.amount)}</td>
                    </tr>
                  ))}
                  {licenseRows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-dark)' }}>Keine Zugangslizenzen</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Kurslizenzen</h2>
            <div className="table-container">
              <table>
                <thead><tr><th>Nutzer</th><th>Schulung</th><th>Zugewiesen</th><th>Laufzeit bis</th><th>Status</th><th style={{ textAlign: 'right' }}>Betrag</th></tr></thead>
                <tbody>
                  {courseRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.userName}</td><td>{r.training}</td><td>{r.from}</td><td>{r.to}</td><td>{r.status}</td>
                      <td style={{ textAlign: 'right', color: r.amount < 0 ? 'var(--primary-dark)' : 'inherit' }}>{fmtEUR(r.amount)}</td>
                    </tr>
                  ))}
                  {courseRows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--gray-dark)' }}>Keine Kurslizenzen</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <FairUse month={month} />
    </Layout>
  );
}
